import { useState, useEffect, useRef } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const VISIONS = [
  {
    id: 'ecology',
    title: '守护生态',
    subtitle: '元宇宙让地球重新呼吸',
    icon: 'ri-leaf-line',
    color: '#00FF88',
    bg: 'rgba(0,255,136,0.08)',
    image: 'https://public.readdy.ai/ai/img_res/19938e240d67f5c86ce0a9e544149c7b.jpg',
    story: '2040年，全球碳排放降至1990年水平的30%。元宇宙会议取代了90%的商务出行，数字孪生工厂实现了零废料生产，AI气候模型精准预测并干预极端天气。人类第一次真正与自然和谐共处。',
    stats: [
      { label: '碳排放减少', value: '70%' },
      { label: '虚拟会议替代出行', value: '90%' },
      { label: '数字工厂零废料率', value: '98%' },
    ],
    features: ['碳足迹追踪', '数字孪生工厂', '虚拟出行替代', 'AI气候预测', '循环经济链', '绿色能源网'],
    doodle: '🌿',
  },
  {
    id: 'peace',
    title: '维护和平',
    subtitle: '当所有人都能感同身受',
    icon: 'ri-heart-line',
    color: '#00FFCC',
    bg: 'rgba(0,255,204,0.08)',
    image: 'https://readdy.ai/api/search-image?query=world%20peace%20holographic%20earth%20glowing%20connections%20between%20countries%20people%20holding%20hands%20digital%20network%20unity%20diversity%20metaverse%20concept%20art%20beautiful%208k&width=800&height=500&seq=meta-vision-peace-001&orientation=landscape',
    story: '2038年，联合国在元宇宙中召开首届"全球公民大会"，来自195个国家的1000万普通市民以数字分身参与。当叙利亚难民的VR体验被全球领导人亲身感受，战争决策变得无比沉重。元宇宙让共情成为外交的基础。',
    stats: [
      { label: '武装冲突减少', value: '65%' },
      { label: '元宇宙外交会议', value: '年1200次' },
      { label: '全球公民参与度', value: '3.2亿人' },
    ],
    features: ['VR共情外交', '数字公民大会', '冲突预警AI', '文化互换体验', '全球投票系统', '和平指数追踪'],
    doodle: '☮',
  },
  {
    id: 'equality',
    title: '消除歧视',
    subtitle: '在元宇宙里，你可以是任何人',
    icon: 'ri-user-heart-line',
    color: '#FF6B9D',
    bg: 'rgba(255,107,157,0.08)',
    image: 'https://readdy.ai/api/search-image?query=diverse%20people%20digital%20avatars%20metaverse%20equality%20inclusion%20colorful%20holographic%20world%20no%20discrimination%20unity%20celebration%20concept%20art%20vibrant%208k&width=800&height=500&seq=meta-vision-equality-001&orientation=landscape',
    story: '2036年，元宇宙中的数字身份让人们可以选择任何外貌、性别、年龄进行交流。当一个残疾人以健全的数字分身参加马拉松，当一个老人以年轻的形象重返职场，偏见开始瓦解。真正的平等，从理解彼此的体验开始。',
    stats: [
      { label: '职场歧视投诉减少', value: '58%' },
      { label: '残障人士就业率提升', value: '340%' },
      { label: '跨文化婚姻增加', value: '220%' },
    ],
    features: ['匿名数字身份', '无障碍元宇宙', '反歧视AI监测', '文化体验交换', '平等就业平台', '包容性设计'],
    doodle: '🌈',
  },
  {
    id: 'environment',
    title: '减少污染',
    subtitle: '循环经济的数字革命',
    icon: 'ri-recycle-line',
    color: '#FFD700',
    bg: 'rgba(255,215,0,0.08)',
    image: 'https://readdy.ai/api/search-image?query=circular%20economy%20digital%20recycling%20smart%20city%20clean%20energy%20zero%20waste%20futuristic%20technology%20green%20innovation%20concept%20art%20glowing%208k&width=800&height=500&seq=meta-vision-environment-001&orientation=landscape',
    story: '2039年，区块链追踪每一件商品从原材料到回收的全生命周期。消费者扫描产品即可看到其碳足迹，AI优化全球供应链减少70%的运输浪费。数字孪生城市实时监测并优化能源使用，城市能耗降低45%。',
    stats: [
      { label: '全球垃圾减少', value: '55%' },
      { label: '供应链浪费减少', value: '70%' },
      { label: '城市能耗降低', value: '45%' },
    ],
    features: ['碳足迹区块链', '智能废物分类', '数字孪生城市', '循环供应链', '绿色消费激励', '污染实时监测'],
    doodle: '♻',
  },
  {
    id: 'culture',
    title: '文化融合',
    subtitle: '人类文明的大融合时代',
    icon: 'ri-global-line',
    color: '#A855F7',
    bg: 'rgba(168,85,247,0.08)',
    image: 'https://readdy.ai/api/search-image?query=cultural%20fusion%20metaverse%20world%20music%20art%20dance%20food%20traditions%20blending%20holographic%20global%20celebration%20diversity%20unity%20beautiful%20concept%20art%208k&width=800&height=500&seq=meta-vision-culture-001&orientation=landscape',
    story: '2041年，元宇宙中的"文明博物馆"收录了人类5000年的文化遗产，任何人都可以身临其境地体验古埃及的神庙、宋朝的市集、玛雅的天文台。AI实时翻译消除了语言障碍，全球艺术家在同一个数字画布上共同创作。人类文明第一次真正融为一体。',
    stats: [
      { label: '数字文化遗产', value: '500万件' },
      { label: '跨语言实时翻译', value: '200种语言' },
      { label: '全球协作艺术项目', value: '年10万个' },
    ],
    features: ['文明博物馆', '实时AI翻译', '跨文化协作', '非遗数字化', '全球艺术平台', '文化体验交换'],
    doodle: '🌍',
  },
];

const IMPACT_STATS = [
  { value: '70%', label: '碳排放减少', icon: 'ri-leaf-line', color: '#00FF88' },
  { value: '65%', label: '武装冲突减少', icon: 'ri-heart-line', color: '#00FFCC' },
  { value: '58%', label: '职场歧视减少', icon: 'ri-user-heart-line', color: '#FF6B9D' },
  { value: '55%', label: '全球垃圾减少', icon: 'ri-recycle-line', color: '#FFD700' },
  { value: '500万', label: '数字文化遗产', icon: 'ri-global-line', color: '#A855F7' },
  { value: '2087', label: '人类文明新纪元', icon: 'ri-rocket-line', color: '#FF6B35' },
];

export default function MetaVision() {
  const [activeVision, setActiveVision] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: statsRef, visibleItems: statsVisible } = useStaggerAnimation(IMPACT_STATS.length, { threshold: 0.08 });
  const { ref: tabsRef, isVisible: tabsVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation({ threshold: 0.06 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.08 });

  useEffect(() => {
    if (autoPlay) {
      autoRef.current = setInterval(() => {
        setActiveVision((v) => (v + 1) % VISIONS.length);
      }, 5000);
    }
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [autoPlay]);

  const vision = VISIONS[activeVision];

  return (
    <section id="meta-vision" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #020810 0%, #030A12 50%, #020810 100%)' }}>
      {/* Watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=metaverse%20utopia%20future%20world%20peace%20ecology%20equality%20culture%20fusion%20holographic%20earth%20glowing%20beautiful%20concept%20art%20ultra%20wide%208k%20cinematic&width=1920&height=1080&seq=meta-vision-hero-bg-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-6"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(0,255,136,0.05) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-8" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,136,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.08) 1px, transparent 1px)',
          backgroundSize: '65px 65px',
        }} />
        {/* Mr. Doodle decorations */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-15" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <path d="M0,50 Q360,20 720,60 Q1080,100 1440,40" stroke="#00FF88" strokeWidth="2.5" fill="none" strokeDasharray="12,20" opacity="0.4"/>
          <path d="M0,860 Q360,830 720,870 Q1080,910 1440,850" stroke="#FFD700" strokeWidth="2" fill="none" strokeDasharray="10,16" opacity="0.3"/>
          <circle cx="150" cy="150" r="80" stroke="#00FFCC" strokeWidth="2" fill="none" strokeDasharray="8,14" opacity="0.25"/>
          <circle cx="1290" cy="750" r="100" stroke="#FF6B9D" strokeWidth="2" fill="none" strokeDasharray="8,14" opacity="0.25"/>
          <circle cx="720" cy="450" r="200" stroke="#A855F7" strokeWidth="1" fill="none" strokeDasharray="6,12" opacity="0.12"/>
          <text x="40" y="400" fontSize="36" fill="#00FF88" opacity="0.08" fontFamily="Caveat, cursive" transform="rotate(-20, 40, 400)">元宇宙愿景</text>
          <text x="1050" y="300" fontSize="28" fill="#FFD700" opacity="0.08" fontFamily="Caveat, cursive" transform="rotate(15, 1050, 300)">META66</text>
          <path d="M200,700 L230,680 L210,720 L240,700" stroke="#00FFCC" strokeWidth="2.5" fill="none" opacity="0.3"/>
          <path d="M1200,200 L1230,180 L1210,220 L1240,200" stroke="#FF6B9D" strokeWidth="2.5" fill="none" opacity="0.3"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header - Grand Opening */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}
        >
          <div
            className="inline-flex items-center gap-3 px-6 py-2.5 mb-8 text-sm font-bold"
            style={{
              background: 'linear-gradient(135deg, rgba(0,255,136,0.12), rgba(0,255,204,0.12))',
              border: '2px solid rgba(0,255,136,0.4)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: '#00FF88',
              fontFamily: 'Caveat, cursive',
              fontSize: '1.1rem',
              boxShadow: '0 0 30px rgba(0,255,136,0.15)',
            }}
          >
            <i className="ri-earth-line" />
            元宇宙愿景 · 人类文明的终极使命
            <i className="ri-earth-line" />
          </div>

          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#00FFCC] to-[#FFD700]">
              元宇宙
            </span>
            <span className="block text-3xl md:text-4xl lg:text-5xl mt-2 text-white/90">
              让世界变得更美好
            </span>
          </h2>

          <p className="text-white/60 text-lg max-w-4xl mx-auto leading-relaxed mb-10">
            元宇宙不只是一个虚拟世界——它是人类解决现实问题的终极工具。
            守护生态、维护和平、消除歧视、减少污染、促进文化融合——
            <strong className="text-[#00FF88]">META66</strong>相信，前沿科技与元宇宙将引领人类走向真正的文明新纪元。
          </p>

          {/* Impact Stats */}
          <div
            ref={statsRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12"
          >
            {IMPACT_STATS.map((stat, i) => (
              <div
                key={stat.label}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-300 hover:scale-105"
                style={{
                  background: `${stat.color}08`,
                  border: `1.5px solid ${stat.color}25`,
                  borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                  opacity: statsVisible[i] ? 1 : 0,
                  transform: statsVisible[i] ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                  transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${i * 100}ms`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${stat.color}15`, border: `1.5px solid ${stat.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                  <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
                </div>
                <p className="font-black text-xl" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                <p className="text-white/40 text-xs text-center leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Vision Tabs */}
        <div
          ref={tabsRef as React.RefObject<HTMLDivElement>}
          className="flex flex-wrap gap-2 justify-center mb-10"
          style={{
            opacity: tabsVisible ? 1 : 0,
            transform: tabsVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
          }}
        >
          {VISIONS.map((v, i) => (
            <button
              key={v.id}
              onClick={() => { setActiveVision(i); setAutoPlay(false); }}
              className="flex items-center gap-2 px-4 py-2.5 font-bold transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{
                background: activeVision === i ? v.bg : 'rgba(255,255,255,0.03)',
                border: `2px solid ${activeVision === i ? v.color + '70' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                color: activeVision === i ? v.color : 'rgba(255,255,255,0.4)',
                transform: activeVision === i ? 'scale(1.06)' : 'scale(1)',
                boxShadow: activeVision === i ? `0 0 20px ${v.color}25` : 'none',
                fontFamily: 'Caveat, cursive',
                fontSize: '1rem',
              }}
            >
              <span className="text-lg">{v.doodle}</span>
              <i className={`${v.icon} text-sm`} />
              {v.title}
            </button>
          ))}
        </div>

        {/* Main Vision Display */}
        <div
          ref={mainRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16"
          style={{
            opacity: mainVisible ? 1 : 0,
            transform: mainVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: '16/10', border: `2px solid ${vision.color}30`, borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px', transition: 'border-color 0.5s ease' }}>
            <img
              key={vision.id}
              src={vision.image}
              alt={vision.title}
              className="w-full h-full object-cover transition-all duration-700"
            />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, rgba(2,8,16,0.8) 100%)` }} />
            {/* Doodle overlay */}
            <div className="absolute top-4 right-4 text-4xl opacity-40" style={{ color: vision.color, fontFamily: 'Caveat, cursive', transform: 'rotate(10deg)', transition: 'color 0.5s ease' }}>
              {vision.doodle}
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white font-black text-xl" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.3rem', color: vision.color, transition: 'color 0.5s ease' }}>{vision.subtitle}</p>
            </div>
            {/* Auto-play indicator */}
            <div className="absolute top-4 left-4 flex gap-1">
              {VISIONS.map((_, i) => (
                <div
                  key={i}
                  className="h-1 rounded-full transition-all duration-300"
                  style={{
                    width: activeVision === i ? '24px' : '6px',
                    background: activeVision === i ? vision.color : 'rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col justify-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 flex items-center justify-center text-3xl" style={{ background: vision.bg, border: `2px solid ${vision.color}50`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%', transition: 'all 0.5s ease' }}>
                  {vision.doodle}
                </div>
                <div>
                  <h3 className="text-white font-black text-3xl" style={{ fontFamily: 'Orbitron, sans-serif', color: vision.color, transition: 'color 0.5s ease' }}>{vision.title}</h3>
                  <p className="text-white/50 text-sm" style={{ fontFamily: 'Caveat, cursive' }}>{vision.subtitle}</p>
                </div>
              </div>
              <p className="text-white/70 text-base leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>{vision.story}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {vision.stats.map((stat) => (
                <div key={stat.label} className="text-center p-3 rounded-xl" style={{ background: `${vision.color}08`, border: `1px solid ${vision.color}25`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px', transition: 'all 0.5s ease' }}>
                  <p className="font-black text-lg" style={{ color: vision.color, fontFamily: 'Orbitron, sans-serif', transition: 'color 0.5s ease' }}>{stat.value}</p>
                  <p className="text-white/40 text-xs leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Features */}
            <div>
              <p className="text-white/40 text-xs font-bold tracking-wider mb-3">核心功能</p>
              <div className="flex flex-wrap gap-2">
                {vision.features.map((f) => (
                  <span
                    key={f}
                    className="px-3 py-1.5 text-xs font-bold"
                    style={{
                      background: `${vision.color}12`,
                      border: `1px solid ${vision.color}35`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      color: vision.color,
                      fontFamily: 'Caveat, cursive',
                      fontSize: '0.85rem',
                      transition: 'all 0.5s ease',
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={() => setShowDetail(!showDetail)}
              className="flex items-center gap-2 text-sm font-bold cursor-pointer hover:scale-105 transition-all w-fit"
              style={{ color: vision.color, fontFamily: 'Caveat, cursive', fontSize: '1rem' }}
            >
              <i className={showDetail ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
              {showDetail ? '收起详情' : '查看更多详情'}
            </button>
          </div>
        </div>

        {/* Grand CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className="rounded-3xl p-12 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(0,255,136,0.06), rgba(0,255,204,0.06), rgba(255,215,0,0.06))',
            border: '2px solid rgba(0,255,136,0.2)',
            borderRadius: '28px 20px 32px 24px / 20px 32px 24px 28px',
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {/* Doodle decorations */}
          <div className="absolute top-6 left-10 text-4xl opacity-15" style={{ color: '#00FF88', fontFamily: 'Caveat, cursive', transform: 'rotate(-18deg)' }}>🌿</div>
          <div className="absolute top-8 right-16 text-3xl opacity-15" style={{ color: '#FFD700', fontFamily: 'Caveat, cursive', transform: 'rotate(12deg)' }}>☮</div>
          <div className="absolute bottom-6 left-20 text-3xl opacity-15" style={{ color: '#FF6B9D', fontFamily: 'Caveat, cursive', transform: 'rotate(-10deg)' }}>🌈</div>
          <div className="absolute bottom-8 right-10 text-3xl opacity-15" style={{ color: '#A855F7', fontFamily: 'Caveat, cursive', transform: 'rotate(15deg)' }}>🌍</div>

          <h3 className="text-white font-black text-4xl md:text-5xl mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            这不是梦想
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#00FFCC] to-[#FFD700]">
              这是META66的使命
            </span>
          </h3>
          <p className="text-white/60 text-lg max-w-3xl mx-auto leading-relaxed mb-10">
            通过元宇宙、6G超连接、AI、区块链、XR体验等前沿科技的融合，
            META66正在构建一个更绿色、更和平、更平等、更美好的数字文明新世界。
            加入我们，共同书写人类文明最精彩的篇章。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#network"
              onClick={(e) => { e.preventDefault(); document.getElementById('network')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="flex items-center justify-center gap-3 px-10 py-4 text-black font-black text-lg cursor-pointer hover:scale-105 transition-all whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #00FF88, #00FFCC)', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '1.3rem' }}
            >
              <i className="ri-rocket-line" />
              探索核心技术
            </a>
            <a
              href="#future-tech"
              onClick={(e) => { e.preventDefault(); document.getElementById('future-tech')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="flex items-center justify-center gap-3 px-10 py-4 text-white font-bold text-base cursor-pointer hover:scale-105 transition-all whitespace-nowrap"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}
            >
              <i className="ri-brain-line" />
              前沿科技矩阵
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
